/**
 * Wire codec for the room WebSocket.
 *
 * Two details of the generated bindings need a real boundary rather than a
 * cast at every call site:
 *
 * 1. `RoomEpoch` and `Revision` are `u64` newtypes. ts-rs types them as
 *    `bigint`, but serde serializes them as plain JSON numbers, so
 *    `JSON.parse` produces `number` and `JSON.stringify` refuses `bigint`.
 *    Decoding converts those two fields to `bigint`; encoding converts them
 *    back. Both are monotonic counters (process starts, accepted mutations),
 *    orders of magnitude below `Number.MAX_SAFE_INTEGER`, so the conversion is
 *    exact.
 * 2. A command is a `ClientMessage` variant *minus* the two fields only the
 *    connection can fill in: `command_id` (correlation) and `epoch` (the room
 *    generation the caller observed). `CommandIntent` derives that shape from
 *    the generated union so command payloads are never re-declared by hand.
 *
 * Validation here is deliberately narrow: this socket carries our own
 * server's ts-rs-generated messages over a same-origin, session-authenticated
 * connection. The decoder checks the discriminant, the protocol version, and
 * the fields it converts — enough that a version mismatch or a truncated
 * frame is a clear error instead of an `undefined` deep in the UI.
 */

import type { ClientMessage, CommandId, ServerMessage } from '../generated';

/** Protocol version this bundle implements (`server/src/protocol.rs`). */
export const CLIENT_PROTOCOL_VERSION = 1;

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** Any `ClientMessage` variant that is an authorized command. */
type CommandMessage = Extract<ClientMessage, { command_id: CommandId }>;

/**
 * A command as the UI expresses it; `RoomClient` adds `command_id` and the
 * current `epoch`.
 */
export type CommandIntent = DistributiveOmit<CommandMessage, 'command_id' | 'epoch'>;

/** Discriminant of a command, e.g. `'seek'`. */
export type CommandKind = CommandIntent['type'];

/** Untrusted local playback telemetry (not a command). */
export type PlaybackReportMessage = Extract<ClientMessage, { type: 'playback-report' }>;

/**
 * One broadcast reaction: the `ServerMessage::Reaction` payload without its
 * discriminant, so the social layer never re-declares the wire shape.
 */
export type ReactionEvent = DistributiveOmit<
  Extract<ServerMessage, { type: 'reaction' }>,
  'type'
>;

export type DecodeResult =
  | { readonly ok: true; readonly message: ServerMessage }
  | { readonly ok: false; readonly reason: string; readonly message: string };

const SERVER_MESSAGE_TYPES: Record<string, true> = {
  snapshot: true,
  'command-ack': true,
  'clock-pong': true,
  error: true,
  // Unsolicited social pushes (phase 04): broadcast to every connection
  // including the sender, never an ack for a specific command.
  chat: true,
  reaction: true,
};

/**
 * `u64` wire fields that must become `bigint` to match the generated types.
 * `expected_revision` is client→server only and listed for symmetry.
 */
const BIGINT_FIELDS: Record<string, true> = {
  epoch: true,
  revision: true,
  expected_revision: true,
};

export function decodeServerMessage(text: string): DecodeResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text, (key, value: unknown) =>
      BIGINT_FIELDS[key] === true && typeof value === 'number' ? BigInt(value) : value,
    );
  } catch {
    return {
      ok: false,
      reason: 'malformed-frame',
      message: 'The server sent a frame this client could not parse.',
    };
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return {
      ok: false,
      reason: 'malformed-frame',
      message: 'The server sent a frame that is not a protocol message.',
    };
  }

  const candidate = parsed as { type?: unknown; protocol_version?: unknown; epoch?: unknown };
  if (typeof candidate.type !== 'string' || SERVER_MESSAGE_TYPES[candidate.type] !== true) {
    return {
      ok: false,
      reason: 'unknown-message',
      message: 'The server sent a message type this client does not understand.',
    };
  }

  if (candidate.type === 'snapshot') {
    if (candidate.protocol_version !== CLIENT_PROTOCOL_VERSION) {
      return {
        ok: false,
        reason: 'protocol-version-mismatch',
        message:
          `This page speaks protocol version ${CLIENT_PROTOCOL_VERSION} but the server ` +
          `speaks ${String(candidate.protocol_version)}. Reload the page to get the ` +
          'matching client.',
      };
    }
    if (typeof candidate.epoch !== 'bigint') {
      return {
        ok: false,
        reason: 'malformed-frame',
        message: 'The server sent a snapshot without a usable room epoch.',
      };
    }
  }

  return { ok: true, message: parsed as ServerMessage };
}

export function encodeClientMessage(message: ClientMessage): string {
  return JSON.stringify(message, (_key, value: unknown) =>
    typeof value === 'bigint' ? Number(value) : value,
  );
}
